/**
 * Conditions attached to an Assembly Reference. A condition gates whether the
 * reference renders, based on the prompt's answer. The schema mirrors
 * AssemblyReferenceCondition / AssemblyReferenceCategoryCondition in
 * Toolkit.Projects and the matching Playbook Marketplace contracts.
 */

/** Comparison operators for decimal/date conditions. Values match the backend enum names. */
export enum RangeComparisonOperator {
  Equal = "equal",
  GreaterThan = "greaterThan",
  LessThan = "lessThan",
  GreaterThanOrEqual = "greaterThanOrEqual",
  LessThanOrEqual = "lessThanOrEqual",
  Range = "range",
}

/** Whether a Flow assembly reference applies flow-wide or per-document. */
export enum AssemblyReferenceType {
  FlowWide = "flowWide",
  Document = "document",
}

export interface AssemblyReferenceCategoryConditionWrite {
  fuzzyMatch: boolean;
  text: string;
}

export interface AssemblyReferenceCategoryCondition extends AssemblyReferenceCategoryConditionWrite {
  objectIdentifier: string;
  assemblyReferenceConditionObjectIdentifier: string;
}

export interface AssemblyReferenceConditionWrite {
  /** Boolean prompts: the expected yes/no answer. */
  isYes?: boolean | null;
  fuzzyMatch: boolean;
  /** Range-of-values prompts. */
  minimumDecimalAnswer?: number | null;
  maximumDecimalAnswer?: number | null;
  /** Text prompts. */
  textAnswer?: string | null;
  /** List/category prompts: require every category condition to match. */
  allCategoriesMatch: boolean;
  /** Decimal comparison. */
  decimalAnswer?: number | null;
  decimalOperator?: RangeComparisonOperator | null;
  /** Date comparison. */
  dateOperator?: RangeComparisonOperator | null;
  startDate?: string | null;
  endDate?: string | null;
  dateAnswer?: string | null;
  /**
   * The prompt whose answer this condition is evaluated against. Required —
   * must belong to the same group as the reference's prompt. Defaults to the
   * reference's own prompt in the UI.
   */
  targetPromptObjectIdentifier: string;
  categoryConditions: AssemblyReferenceCategoryConditionWrite[];
}

export interface AssemblyReferenceCondition {
  objectIdentifier: string;
  assemblyReferenceObjectIdentifier: string;
  isYes?: boolean | null;
  fuzzyMatch: boolean;
  minimumDecimalAnswer?: number | null;
  maximumDecimalAnswer?: number | null;
  textAnswer?: string | null;
  allCategoriesMatch: boolean;
  decimalAnswer?: number | null;
  decimalOperator?: RangeComparisonOperator | null;
  dateOperator?: RangeComparisonOperator | null;
  startDate?: string | null;
  endDate?: string | null;
  dateAnswer?: string | null;
  targetPromptObjectIdentifier: string;
  categoryConditions: AssemblyReferenceCategoryCondition[];
}
